<script setup lang="ts">
import type { Schemas } from '#shopware'
import { getProductRoute, getTranslatedProperty } from '@shopware/helpers'
import { useRequestURL } from '#imports'

const props = defineProps<{
    /**
     * The products currently rendered on this page, in display order. Google wants the list
     * to mirror the page ("Mark up all items that are on the summary or category page"), and
     * paginated categories each carry their own ItemList for the products on that page.
     */
    products: Schemas['Product'][]
}>()

// Google's carousel beta needs at least three entities to be eligible; below that the
// markup is pointless, so nothing is emitted.
const MIN_ITEMS = 3

const localePath = useLocalePath()
const { currencyCode } = usePrice()
const runtimeConfig = useRuntimeConfig()
const { cardImageUrl } = useProductImageUrl()

// Captured once at setup so item URLs are absolute. useRequestURL() must not be called
// inside a reactive head getter (see useCanonicalUrl).
const origin = import.meta.server ? useRequestURL().origin : window.location.origin

// Country instances are served under a URL prefix (BASEPATH) that the router paths omit.
const basePath = (runtimeConfig.app?.baseURL || '/') === '/' ? '' : (runtimeConfig.app?.baseURL || '')

/**
 * The offer for a listed product. `useProductPriceCustom` is a pure wrapper of computeds over
 * the passed product, so it is safe to call per item — and reusing it keeps the schema price
 * identical to the price the card renders instead of restating the parent/tier-price rules.
 * `displayFrom` means the shown price is the lowest of a range, which maps to AggregateOffer.
 */
function buildOffers (product: Schemas['Product']): Record<string, unknown> | undefined {
    const { unitPrice, displayFrom } = useProductPriceCustom(computed(() => product))
    if (unitPrice.value == null) return undefined

    return displayFrom.value
        ? { '@type': 'AggregateOffer', lowPrice: unitPrice.value, priceCurrency: currencyCode.value }
        : { '@type': 'Offer', price: unitPrice.value, priceCurrency: currencyCode.value }
}

function buildItem (product: Schemas['Product']): Record<string, unknown> {
    const item: Record<string, unknown> = {
        '@type': 'Product',
        name: getTranslatedProperty(product, 'name'),
        url: `${origin}${basePath}${localePath(getProductRoute(product).path)}`,
    }

    const image = cardImageUrl(product)
    if (image) item.image = image

    const offers = buildOffers(product)
    if (offers) item.offers = offers

    return item
}

const itemListSchema = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: props.products.map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: buildItem(product),
    })),
}))

useHead(() => (props.products.length >= MIN_ITEMS
    ? { script: [jsonLdScript(itemListSchema.value, 'ld-item-list')] }
    : {}))
</script>

<template>
    <div class="hidden" />
</template>
